/**
 * Normalizes stocks report API payloads (Laravel paginator vs { data: rows[], pagination, meta }).
 */
export function normalizeStockReportTablePayload(payload) {
  if (Array.isArray(payload)) {
    return {
      data: payload,
      total: payload.length,
      per_page: payload.length,
      current_page: 1,
    };
  }

  const p = payload || {};

  // Shape: { status, data: Row[], pagination?, meta? } — data is the row array
  if (Array.isArray(p.data)) {
    const pag = p.pagination || p.meta || {};
    const total = pag.total ?? pag.totalCount ?? p.data.length;
    return {
      data: p.data,
      total: Number(total) || p.data.length,
      per_page: pag.perPage ?? pag.per_page ?? 10,
      current_page: pag.page ?? pag.current_page ?? pag.currentPage ?? 1,
    };
  }

  // Shape: { table: { data: Row[] } } or nested table
  const table = p.table ?? p.data?.table;
  if (table && Array.isArray(table.data)) {
    const pag = table.pagination || table.meta || p.pagination || p.meta || {};
    return {
      data: table.data,
      total: Number(pag.total ?? table.total ?? table.data.length) || table.data.length,
      per_page: pag.perPage ?? pag.per_page ?? 10,
      current_page: pag.page ?? pag.current_page ?? pag.currentPage ?? 1,
    };
  }

  // Shape: Laravel-style { data: { data: Row[], total, ... } }
  const inner = p.data && typeof p.data === 'object' && !Array.isArray(p.data) ? p.data : p;
  const rows = Array.isArray(inner.data) ? inner.data : [];
  const pag = p.pagination || p.meta || inner;
  return {
    ...inner,
    data: rows,
    total: Number(inner.total ?? pag.total ?? rows.length) || rows.length,
    per_page: inner.per_page ?? inner.perPage ?? pag.per_page ?? 10,
    current_page: inner.current_page ?? inner.currentPage ?? inner.page ?? pag.page ?? 1,
  };
}

function mapStockBucketCounts(source) {
  if (!source || typeof source !== 'object') return null;
  const buckets =
    source.productCountsByBucket ?? source.product_counts_by_bucket;
  if (!buckets || typeof buckets !== 'object') return null;
  return {
    inStock: Number(buckets.in_stock ?? buckets.inStock ?? 0) || 0,
    lowStock: Number(buckets.low_stock ?? buckets.lowStock ?? 0) || 0,
    outOfStock: Number(buckets.out_of_stock ?? buckets.outOfStock ?? 0) || 0,
    lowThreshold: source.lowThreshold ?? source.low_threshold,
    note: source.note,
  };
}

export function normalizeStockReportSummaryPayload(payload) {
  const p = payload || {};
  if (p.summary && typeof p.summary === 'object') {
    const mapped = mapStockBucketCounts(p.summary);
    if (mapped) return mapped;
    return p.summary;
  }
  if (p.data?.summary && typeof p.data.summary === 'object') {
    const mapped = mapStockBucketCounts(p.data.summary);
    if (mapped) return mapped;
    return p.data.summary;
  }
  if (p.data && typeof p.data === 'object' && !Array.isArray(p.data)) {
    const mapped = mapStockBucketCounts(p.data);
    if (mapped) return mapped;
    const d = p.data;
    if (
      d.inStock != null ||
      d.in_stock != null ||
      d.lowStock != null ||
      d.low_stock != null ||
      d.outOfStock != null ||
      d.out_of_stock != null
    ) {
      return {
        inStock: Number(d.inStock ?? d.in_stock ?? 0) || 0,
        lowStock: Number(d.lowStock ?? d.low_stock ?? 0) || 0,
        outOfStock: Number(d.outOfStock ?? d.out_of_stock ?? 0) || 0,
        lowThreshold: d.lowThreshold ?? d.low_threshold,
        note: d.note,
      };
    }
  }
  const mapped = mapStockBucketCounts(p.summary || p.data || p);
  return mapped || p.summary || p.data || {};
}
